import { Component, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EVENT_LABELS, INITIAL_LOADER_STATES } from '@app-live-view/constants/live-view.constants';
import { RequestWebRTCLivestreamResp, DeviceUsageResponse } from '@app-core/models/livestream';
import { AccessService } from '@app-core/services/access/access.service';
import { LivestreamService } from '@app-core/services/livestream/livestream.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, finalize, take, takeUntil } from 'rxjs/operators';
import { TimerState } from '@app-shared/components/timer/timer.component';
import { DataService } from '@app-core/services/data/data.service';
import { Socket } from 'socket.io-client';
import { LivestreamTimerComponent } from '@app-shared/components/livestream-timer/livestream-timer.component';
import { CallState, SessionSummary, SOUNDS, DeviceAudioAlert, DeviceStatePayload } from '@app-live-view/constants/live-view.constants';
import { LiveStreamSdkService } from '@app-live-view/services/webrtc-sdk/live-stream-sdk.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { SESSION_TYPES, STREAM_MODES } from '@rideview/livestream-web-sdk';

interface AudioCallModalData {
  assetId?: string;
  deviceId?: string;
  tripId: string;
  asset: any;
  socketConnection: Socket;
}

// todo - name every file related to webrtc streaming as - WebRTCLiveStream
@Component({
  selector: 'app-webrtc-audio-call-modal',
  templateUrl: './webrtc-audio-call-modal.component.html',
  styleUrls: ['./webrtc-audio-call-modal.component.scss'],
})
export class WebRTCAudioCallModalComponent implements OnInit, OnDestroy {
  @ViewChild('callDurationTimer', { static: true })
  private callDurationTimer: LivestreamTimerComponent;

  @ViewChild('audioPlayer', { static: true }) audioPlayer: any;
  public remoteVolume = 0;
  private audioContext: AudioContext | null = null;
  private remoteAnalyser: AnalyserNode | null = null;
  private remoteAnimationId: number | null = null;
  public callDetails: RequestWebRTCLivestreamResp | null = null;
  public errorMessage: string | null = null;
  public isInitiatingCall = false;
  public verifySessionInterval = 9 * 60 + 45;
  public verifySession = false;
  public enabledRetryOnFailure = true;
  public fleetId: string;
  public noActionTimerState: TimerState = {
    count: true,
    countup: false,
    value: 15,
    speed: 1000,
    increase: 1,
    pauseAt: 0,
  };
  public deviceUsageLoader: boolean = true;
  public deviceUsage: number = 0;
  public baseMonthlySecondsLoader = true;
  public baseMonthlySeconds = 0;
  public infoMessage: string | null = null;
  private ngUnsubscribe = new Subject<void>();
  public callError = false;
  public loaderStates = INITIAL_LOADER_STATES();
  public showSessionSummary = false;
  public sessionSummaryLoading = false;
  public sessionSummaryData: SessionSummary | null = null;
  public callState: CallState = CallState.IDLE;
  public callDuration = '00:00:00';
  public isMuted = false;
  public micPermissionError = false;
  public isBusyConflict = false;
  public isPanicButtonMissing = false;
  public toast = {
    message: '',
    visible: false,
  };
  public bars: number[] = [6, 10, 14, 10, 6];
  private callTimerInterval: ReturnType<typeof setInterval> | null = null;
  private callStartTime = 0;
  private callTimeout: ReturnType<typeof setTimeout> | null = null;
  private endConnectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private toastTimeout: ReturnType<typeof setTimeout> | null = null;
  private audio: HTMLAudioElement | null = null;
  private spacebarAudio: HTMLAudioElement | null = null;
  private readonly CALL_TIMEOUT_MS = 35000;
  private micBarsAnimationId: number | null = null;
  private micBarsStarting = false;
  private micBarsAnalyser: AnalyserNode | null = null;
  private micBarsStream: MediaStream | null = null;
  private smoothedMicScale = 0;
  private smoothedRemoteVolume = 0;
  private remoteTrackListenerStream: MediaStream | null = null;
  private isCallEnded = false;
  private remoteSourceNode: MediaStreamAudioSourceNode | null = null;
  private micSourceNode: MediaStreamAudioSourceNode | null = null;
  private isSpacebarPressed = false;
  private isMicButtonPressed = false;
  private readonly REMOTE_AUDIO_NOISE_FLOOR = 10;
  private deviceConnected = false;
  private audioRecordingEnabled: boolean | null = null;
  private callInitiationTriggered = false;

  constructor(
    public dialogRef: MatDialogRef<WebRTCAudioCallModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AudioCallModalData,
    private livestreamService: LivestreamService,
    public translate: TranslateService,
    private accessService: AccessService,
    private dataService: DataService,
    private liveStreamSdkService: LiveStreamSdkService,
    private snackBarService: SnackBarService
  ) {}

  public ngOnInit() {
    this.initMicListener();
    this.registerCallStatusEvents();
    this.registerNetworkAndSummaryEvents();
    this.registerAlertAndMuteEvents();
    this.registerLeaveAndErrorEvents();
    this.subscribeToConnectionState();
    this.subscribeToDeviceState();
    this.getDeviceUsage();
    this.getDeviceBaseMonthlySeconds();
    this.dataService._currentFleet.pipe(filter(Boolean), take(1), takeUntil(this.ngUnsubscribe)).subscribe((value: string) => {
      this.fleetId = value;
      this.initiateCall();
    });
  }

  private registerCallStatusEvents() {
    this.liveStreamSdkService.callResponse$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((payload) => {
      try {
        const status = payload?.status;
        if (status === 'ACCEPTED') {
          if (this.callState !== CallState.CALLING) {
            return;
          }
          this.handleCallState('accepted');
        } else if (status === 'IGNORED') {
          this.handleCallState('ignored');
        }
      } catch (err) {
        console.error('callResponse$ handler failed:', err);
      }
    });

    this.liveStreamSdkService.callEnd$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.isCallEnded = true;
      this.handleCallState('disconnected');
    });
  }

  private registerNetworkAndSummaryEvents() {
    this.liveStreamSdkService.callSummary$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((summary) => {
      this.sessionSummaryData = summary;
      this.sessionSummaryLoading = false;
      this.showSessionSummary = true;
    });
  }

  private registerAlertAndMuteEvents() {
    this.liveStreamSdkService.deviceAudioAlert$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((payload) => {
      this.handleDeviceAudioAlert(payload);
    });

    this.liveStreamSdkService.muteStatus$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isMuted) => {
      this.isMuted = isMuted;
      if (isMuted) {
        this.stopMicBarsAnimation();
        this.resetBars();
        this.destroyMicBarsMonitor();
      } else if (this.callState === CallState.ACTIVE) {
        this.startMicBarsAnimation();
      }
    });
  }

  private registerLeaveAndErrorEvents() {
    this.liveStreamSdkService.leave$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.showToast('Call Disconnected');
      this.setCallState(CallState.IDLE);
      this.onCloseAction();
    });

    this.liveStreamSdkService.error$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((err) => {
      if (err?.name === 'NotAllowedError') {
        this.micPermissionError = true;
        this.showToast('Microphone permission denied. Please enable mic access.');
      }
    });
  }

  @HostListener('window:beforeunload', ['$event'])
  public ngOnDestroy() {
    this.isSpacebarPressed = false;
    this.isCallEnded = true;
    this.clearEndConnectionTimeout();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();

    this.liveStreamSdkService.stopAudioCall().catch(() => null);
    this.liveStreamSdkService
      .stopStreaming()
      .then(() => null)
      .catch(() => null);
    this.snackBarService.success(this.translate.instant('Call Ended'));

    this.liveStreamSdkService.deactivateMic().catch(() => null);
    this.clearCallTimeout();
    this.stopCallTimer();
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }
    this.stopSound();
    this.audio = null;
    if (this.spacebarAudio) {
      this.spacebarAudio.pause();
      this.spacebarAudio.src = '';
      this.spacebarAudio = null;
    }
    if (this.audioPlayer && this.audioPlayer.nativeElement) {
      this.audioPlayer.nativeElement.srcObject = null;
      this.audioPlayer.nativeElement.load();
    }
    this.stopRemoteAnalyserLoop();
    this.stopMicBarsAnimation();
    this.destroyMicBarsMonitor();
    if (this.audioContext) {
      this.audioContext.close().catch(() => null);
    }
  }

  @HostListener('window:keydown', ['$event'])
  public onSpacebarKeyDown(event: KeyboardEvent) {
    if (event.code !== 'Space' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    if (this.isSpacebarPressed) {
      return;
    }
    this.isSpacebarPressed = true;
    if (this.callState === CallState.ACTIVE && this.isMuted && !this.micPermissionError) {
      this.playSpacebarSound();
      this.liveStreamSdkService.unmuteAudio();
    }
  }

  @HostListener('window:keyup', ['$event'])
  public onSpacebarKeyUp(event: KeyboardEvent) {
    if (event.code !== 'Space' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.isSpacebarPressed = false;
    if (this.callState === CallState.ACTIVE && !this.isMuted && !this.micPermissionError) {
      this.playSpacebarSound();
      this.liveStreamSdkService.muteAudio();
    }
  }

  private async getAudioContext(): Promise<AudioContext> {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
    }
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume().catch(() => null);
    }
    return this.audioContext;
  }

  private setupRemoteAudioIfAvailable() {
    if (this.remoteAnalyser) {
      return;
    }

    const remoteStream = this.liveStreamSdkService.getRemoteStream();
    if (!remoteStream) {
      return;
    }
    this.setupRemoteAudioAnalyzer(remoteStream).catch(() => null);
  }

  private async setupRemoteAudioAnalyzer(stream: MediaStream) {
    if (this.remoteAnalyser) {
      return;
    }

    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) {
      if (this.remoteTrackListenerStream === stream) {
        return;
      }
      this.remoteTrackListenerStream = stream;
      const onAddTrack = (event: MediaStreamTrackEvent) => {
        if (event.track?.kind === 'audio') {
          this.remoteTrackListenerStream = null;
          this.setupRemoteAudioAnalyzer(stream).catch(() => null);
        }
      };
      stream.addEventListener('addtrack', onAddTrack, { once: true });
      return;
    }

    this.remoteTrackListenerStream = null;
    this.stopRemoteAnalyserLoop();

    const audioCtx = await this.getAudioContext();
    this.remoteSourceNode?.disconnect();
    this.remoteSourceNode = audioCtx.createMediaStreamSource(stream);
    this.remoteAnalyser = audioCtx.createAnalyser();
    this.remoteAnalyser.fftSize = 256;
    this.remoteAnalyser.smoothingTimeConstant = 0.8;
    this.remoteSourceNode.connect(this.remoteAnalyser);
    this.startRemoteAnalyserLoop();
  }

  private startRemoteAnalyserLoop() {
    const analyser = this.remoteAnalyser; // capture current instance
    if (!analyser) return;

    const data = new Uint8Array(analyser.frequencyBinCount);
    const animate = () => {
      if (!this.remoteAnalyser || this.remoteAnalyser !== analyser) {
        return;
      }
      if (this.callState !== CallState.ACTIVE) {
        this.stopRemoteAnalyserLoop();
        return;
      }
      if (this.audioContext?.state === 'suspended') {
        this.audioContext.resume().catch(() => null);
      }
      analyser.getByteFrequencyData(data);
      let sumSquares = 0;
      for (let i = 0; i < data.length; i++) {
        sumSquares += data[i] * data[i];
      }
      const volume = Math.sqrt(sumSquares / data.length);
      this.smoothedRemoteVolume = this.smoothedRemoteVolume * 0.85 + volume * 0.15;
      this.remoteVolume = this.smoothedRemoteVolume;
      this.remoteAnimationId = requestAnimationFrame(animate);
    };
    this.remoteAnimationId = requestAnimationFrame(animate);
  }

  private stopRemoteAnalyserLoop() {
    if (this.remoteAnimationId !== null) {
      cancelAnimationFrame(this.remoteAnimationId);
      this.remoteAnimationId = null;
    }
    this.remoteSourceNode?.disconnect();
    this.remoteSourceNode = null;
    this.remoteAnalyser = null;
    this.remoteTrackListenerStream = null;
    this.smoothedRemoteVolume = 0;
    this.remoteVolume = 0;
  }

  private initMicListener() {
    if (!navigator.permissions || !navigator.permissions.query) return;

    navigator.permissions.query({ name: 'microphone' as PermissionName }).then((status) => {
      this.micPermissionError = status.state === 'denied';

      status.onchange = () => {
        this.micPermissionError = status.state === 'denied';
        if (status.state === 'granted' && this.callState === CallState.ACTIVE) {
          this.activateMicNow();
        }
      };
    });
  }

  private async activateMicNow() {
    try {
      await this.liveStreamSdkService.activateMic();
      this.liveStreamSdkService.muteAudio();
      this.micPermissionError = false;
    } catch (e) {
      console.error('Delayed activation failed:', e);
    }
  }

  private handleCallState(state: string): void {
    this.clearCallTimeout();

    switch (state) {
      case 'accepted':
        this.setCallState(CallState.ACTIVE);
        break;

      case 'ignored':
        this.endCall();
        this.playSound('ignore');
        this.showToast('Call Ignored');
        break;

      case 'disconnected':
        this.endCall();
        this.showToast('Call Ended');
        break;
    }
  }
  public onStartCall(): void {
    this.clearEndConnectionTimeout();
    if (this.isInitiatingCall) {
      return;
    }

    if (!this.callDetails) {
      this.initiateCall();
      return;
    }

    this.resetMuteStateForNewCall();
    this.callDurationTimer.restartTimer(this.verifySessionInterval);
    this.setCallState(CallState.CALLING);
    this.liveStreamSdkService.startAudioCall().catch(() => {
      this.setCallState(CallState.DISABLED);
      this.errorMessage = 'Failed to initiate call. Please try again';
    });
    this.startCallTimeout();
  }

  private startCallTimeout(): void {
    this.clearCallTimeout();
    this.callTimeout = setTimeout(() => {
      if (this.callState === CallState.CALLING) {
        this.handleCallState('ignored');
      }
    }, this.CALL_TIMEOUT_MS);
  }

  private clearCallTimeout() {
    if (this.callTimeout) {
      clearTimeout(this.callTimeout);
      this.callTimeout = null;
    }
  }
  private startCallTimer() {
    if (this.callTimerInterval) return;

    this.callStartTime = Date.now();
    this.callTimerInterval = setInterval(() => {
      const s = Math.floor((Date.now() - this.callStartTime) / 1000);
      const hours = Math.floor(s / 3600);
      const minutes = Math.floor((s % 3600) / 60);
      const seconds = s % 60;
      this.callDuration =
        `${String(hours).padStart(2, '0')}:` + `${String(minutes).padStart(2, '0')}:` + `${String(seconds).padStart(2, '0')}`;
    }, 1000);
  }

  private stopCallTimer() {
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
  }
  private tearDownActiveAudio(): void {
    this.stopRemoteAnalyserLoop();
    this.stopMicBarsAnimation();
    this.resetBars();
    this.destroyMicBarsMonitor();
    this.stopSound();
    this.callDurationTimer.pause();
    if (this.audioPlayer?.nativeElement) {
      this.audioPlayer.nativeElement.muted = true;
    }
  }

  private setCallState(state: CallState) {
    if (this.callState === state) {
      return;
    }
    this.callState = state;

    switch (state) {
      case CallState.CALLING:
        this.stopRemoteAnalyserLoop();
        this.playSound('calling', true);
        break;

      case CallState.ACTIVE:
        this.stopSound();
        this.showToast('Call Connected');
        this.playSound('answered');
        this.startCallTimer();
        this.callDurationTimer.start();
        if (this.audioContext?.state === 'suspended') {
          this.audioContext.resume().catch(() => null);
        }
        if (this.audioPlayer && this.audioPlayer.nativeElement) {
          this.audioPlayer.nativeElement.muted = false;
          this.setupRemoteAudioIfAvailable();
          if (this.remoteAnalyser && this.remoteAnimationId === null) {
            this.startRemoteAnalyserLoop();
          }
        }
        break;

      case CallState.IDLE:
        this.tearDownActiveAudio();
        this.resetMuteStateForNewCall();
        this.stopCallTimer();
        this.callDuration = '00:00:00';
        this.playSound('end');
        break;

      case CallState.IGNORED:
        this.tearDownActiveAudio();
        this.resetMuteStateForNewCall();
        this.stopCallTimer();
        this.callDuration = '00:00:00';
        this.playSound('ignore');
        this.showToast('Call Ignored');
        this.callState = CallState.IDLE;
        break;

      case CallState.DISABLED:
        this.tearDownActiveAudio();
        this.stopCallTimer();
        break;
    }
  }
  private playSound(type: keyof typeof SOUNDS, loop = false) {
    const src = SOUNDS[type];
    if (!src) return;
    if (!this.audio) {
      this.audio = new Audio();
    }
    this.stopSound();

    this.audio.src = src;
    this.audio.loop = loop;
    this.audio.volume = 0.7;

    this.audio.play().catch(() => {});
  }
  private stopSound() {
    if (this.audio) {
      this.audio.pause();
      this.audio.src = '';
      this.audio.load();
    }
  }

  private playSpacebarSound() {
    const src = SOUNDS['spacebar'];
    if (!src) return;
    if (!this.spacebarAudio) {
      this.spacebarAudio = new Audio(src);
      this.spacebarAudio.volume = 0.7;
    }
    this.spacebarAudio.currentTime = 0;
    this.spacebarAudio.play().catch(() => {});
  }

  public endCall() {
    this.isCallEnded = true;
    this.clearCallTimeout();
    this.clearEndConnectionTimeout();
    this.resetMuteStateForNewCall();
    this.openSessionSummaryLoader();
    this.liveStreamSdkService.deactivateMic().catch(() => null);
    const activeStreamRequestId = this.callDetails?.streamRequestId;
    this.setCallState(CallState.IDLE);

    if (activeStreamRequestId) {
      this.liveStreamSdkService.stopAudioCall().catch(() => null);
      this.endConnectionTimeout = setTimeout(() => {
        this.callDetails = null;
        this.stopCallConnection(activeStreamRequestId);
        this.endConnectionTimeout = null;
      }, 2000);
      return;
    }

    this.endConnectionTimeout = setTimeout(() => {
      this.liveStreamSdkService.stopAudioCall().catch(() => null);
      this.endConnectionTimeout = null;
    }, 1000);
  }

  private clearEndConnectionTimeout() {
    if (this.endConnectionTimeout) {
      clearTimeout(this.endConnectionTimeout);
      this.endConnectionTimeout = null;
    }
  }

  private resetMuteStateForNewCall() {
    this.isMuted = false;
    this.resetBars();
  }

  private openSessionSummaryLoader() {
    this.showSessionSummary = true;
    this.sessionSummaryLoading = true;
    this.sessionSummaryData = null;
  }

  private showToast(message: string): void {
    if (this.toast.message === message && this.toast.visible) return;

    this.toast.message = message;
    this.toast.visible = true;

    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
    }

    this.toastTimeout = setTimeout(() => {
      this.toast.visible = false;
      this.toastTimeout = null;
    }, 3000);
  }
  private handleDeviceAudioAlert(payload: DeviceAudioAlert) {
    if (this.callState !== CallState.ACTIVE) {
      return;
    }
    const alert = payload.eventType;
    if (!alert) {
      return;
    }
    // Only show toast if the alert is present in EVENT_LABELS keys
    if (Object.prototype.hasOwnProperty.call(EVENT_LABELS, alert)) {
      this.showToast(this.translate.instant(EVENT_LABELS[alert]));
    }
  }
  private resetBars() {
    this.bars = [6, 10, 14, 10, 6];
  }

  public get pulseOpacity(): number {
    const level = this.remoteVolume - this.REMOTE_AUDIO_NOISE_FLOOR;
    if (level <= 0) {
      return 0;
    }
    return Math.min(0.6, level / 35);
  }

  public get pulseScale(): number {
    const level = this.remoteVolume - this.REMOTE_AUDIO_NOISE_FLOOR;
    return level <= 0 ? 1 : 1 + level / 80;
  }

  public async onMicPressStart(event?: Event): Promise<void> {
    event?.preventDefault();
    if (this.isMicButtonPressed) {
      return;
    }
    this.isMicButtonPressed = true;

    if (this.micPermissionError) {
      try {
        await this.liveStreamSdkService.activateMic();
        this.micPermissionError = false;
      } catch {
        this.isMicButtonPressed = false;
        this.showToast('Please enable microphone access from the browser address bar.');
        return;
      }
    }

    if (this.callState === CallState.ACTIVE && this.isMuted) {
      this.liveStreamSdkService.unmuteAudio();
    }
  }

  public onMicPressEnd(): void {
    if (!this.isMicButtonPressed) {
      return;
    }
    this.isMicButtonPressed = false;
    if (this.callState === CallState.ACTIVE && !this.isMuted) {
      this.liveStreamSdkService.muteAudio();
    }
  }

  private advanceLoaderToConnecting() {
    this.loaderStates.connecting.inProgress = false;
    this.loaderStates.current = 1;
    this.loaderStates.connected.inProgress = true;

    return new Promise((resolve) => {
      setTimeout(() => {
        this.loaderStates.current = 2;
        this.loaderStates.connected.inProgress = false;
        this.loaderStates.generating.inProgress = true;
        resolve(true);
      }, 1000);
    });
  }

  private subscribeToConnectionState() {
    this.liveStreamSdkService.connectionState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((val) => {
      if (val === 'device-connected' && this.callState === CallState.IDLE) {
        this.deviceConnected = true;
        this.evaluateCallInitiation();
      }
    });
  }

  private subscribeToDeviceState() {
    this.liveStreamSdkService.deviceState$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((payload: DeviceStatePayload) => {
      this.audioRecordingEnabled = !!payload?.audioRecordingEnabled;
      if (!this.audioRecordingEnabled) {
        if (this.callState === CallState.IDLE) {
          this.isInitiatingCall = false;
          this.setCallState(CallState.CONSENT);
        }
        return;
      }
      this.evaluateCallInitiation();
    });
  }

  private async evaluateCallInitiation() {
    if (this.callInitiationTriggered || this.isCallEnded || this.callState !== CallState.IDLE) {
      return;
    }
    if (!this.deviceConnected || this.audioRecordingEnabled !== true) {
      return;
    }
    this.callInitiationTriggered = true;
    await this.advanceLoaderToConnecting();
    if (this.isCallEnded || this.callState !== CallState.IDLE) {
      return;
    }
    this.isInitiatingCall = false;
    this.onStartCall();
  }
  private resetState() {
    this.resetLoaderStates();
    this.isInitiatingCall = true;
    this.errorMessage = null;
    this.infoMessage = null;
    this.enabledRetryOnFailure = true;
    this.isCallEnded = false;
    this.isBusyConflict = false;
    this.isPanicButtonMissing = false;
    this.deviceConnected = false;
    this.audioRecordingEnabled = null;
    this.callInitiationTriggered = false;
    this.callDurationTimer.pause();
  }

  private async initiateCall() {
    this.resetState();
    this.loaderStates.initiated = false;
    if (!this.fleetId) {
      this.isInitiatingCall = false;
      this.loaderStates.initiated = true;
      this.errorMessage = 'Failed to initiate call. Please try again';
      return;
    }

    try {
      if (this.callDetails) {
        await this.stopCallConnection(this.callDetails.streamRequestId);
      }
      this.callDetails = await this.liveStreamSdkService.startStreaming(
        {
          fleetId: this.fleetId,
          deviceId: this.data.deviceId,
          userId: this.accessService.getLoginInfo().userId,
          sessionType: SESSION_TYPES.AUDIO_CALL,
        },
        STREAM_MODES.WEBRTC
      );
      this.attachAudioPlayerAndAnalyser();
    } catch (err) {
      this.handleInitiateCallError(err);
    } finally {
      this.loaderStates.initiated = true;
    }
  }

  private attachAudioPlayerAndAnalyser() {
    if (!this.audioPlayer?.nativeElement) {
      return;
    }
    const audioEl = this.audioPlayer.nativeElement as HTMLAudioElement;
    this.liveStreamSdkService.attach(audioEl);
    this.setupRemoteAudioIfAvailable();
  }

  private getErrorStatus(err: any): number | undefined {
    return err?.status ?? err?.cause?.status ?? err?.cause?.data?.status ?? err?.response?.status;
  }

  private getErrorCode(err: any): string | undefined {
    return err?.errorCode ?? err?.cause?.errorCode ?? err?.cause?.data?.errorCode ?? err?.response?.data?.errorCode;
  }

  private handleInitiateCallError(err: any) {
    this.isInitiatingCall = false;
    this.callDetails = null;
    const status = this.getErrorStatus(err);
    const errorCode = this.getErrorCode(err);
    if (status === 409) {
      this.isBusyConflict = true;
      this.setCallState(CallState.DISABLED);
    } else if (status === 403 && errorCode === 'DVRL-PBMAP') {
      this.isPanicButtonMissing = true;
      this.setCallState(CallState.DISABLED);
    } else if (status === 403 && (errorCode === 'DVRL-ARCON' || errorCode === 'DVRL-ARDIS')) {
      this.setCallState(CallState.CONSENT);
    } else if (status === 406) {
      this.errorMessage = this.translate.instant('liveStreamModalMonthlyMinsExhausted');
      this.enabledRetryOnFailure = false;
    } else {
      this.setCallState(CallState.DISABLED);
      this.errorMessage = 'Failed to initiate call. Please try again';
    }
  }

  private async stopCallConnection(streamRequestId?: string) {
    if (!streamRequestId) {
      return;
    }

    if (this.audioPlayer?.nativeElement) {
      this.audioPlayer.nativeElement.srcObject = null;
      this.audioPlayer.nativeElement.load();
    }

    this.stopRemoteAnalyserLoop();
    await this.liveStreamSdkService.stopStreaming().catch(() => null);
  }

  public continueStreaming() {
    this.verifySession = false;
  }

  public verifySessionTimerAlert() {
    this.verifySession = true;
    this.playSound('modal', false);
  }
  public onNoActionTimerPause() {
    this.onCloseAction();
  }

  public onRetryCallAction() {
    this.callState = CallState.IDLE;
    this.initiateCall();
  }

  public onCloseAction() {
    this.isCallEnded = true;
    this.dialogRef.close();
  }
  public resetLoaderStates() {
    this.loaderStates = INITIAL_LOADER_STATES();
  }

  public getDeviceUsage() {
    this.deviceUsageLoader = true;
    const { deviceId } = this.data;
    this.livestreamService
      .getDeviceUsage({ deviceId: deviceId })
      .pipe(
        finalize(() => {
          this.deviceUsageLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: DeviceUsageResponse) => {
          const { totalDuration = 0 } = res || {};
          this.deviceUsage = Math.floor(totalDuration / 60);
        },
        () => {
          this.deviceUsage = 0;
        }
      );
  }

  private getDeviceBaseMonthlySeconds() {
    this.baseMonthlySecondsLoader = true;
    const { assetId } = this.data;
    this.livestreamService
      .getAssetDetails({ assetId, includeLiveStreamData: true })
      .pipe(
        finalize(() => {
          this.baseMonthlySecondsLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: any) => {
          const { baseMonthlySeconds = 0, currentMonthLivestreamExtraMinutes = 0 } = res || {};
          const roundedBaseMonthlyMinutes = Math.round(baseMonthlySeconds / 60);
          const totalMinutes = roundedBaseMonthlyMinutes + currentMonthLivestreamExtraMinutes;
          this.baseMonthlySeconds = totalMinutes < 0 ? 0 : totalMinutes;
        },
        () => {
          this.baseMonthlySeconds = 0;
        }
      );
  }
  private async startMicBarsAnimation() {
    if (this.micBarsAnimationId !== null || this.micBarsStarting) {
      return;
    }
    this.micBarsStarting = true;
    try {
      if (!this.micBarsAnalyser) {
        try {
          if (!this.micBarsStream) {
            let localStream = this.liveStreamSdkService.getLocalStream();
            if (!localStream?.getAudioTracks?.().length) {
              await this.liveStreamSdkService.activateMic().catch(() => null);
              localStream = this.liveStreamSdkService.getLocalStream();
            }

            if (localStream?.getAudioTracks?.().length) {
              this.micBarsStream = localStream;
            } else {
              this.resetBars();
              return;
            }
          }
          const audioCtx = await this.getAudioContext();
          this.micSourceNode?.disconnect();
          this.micSourceNode = audioCtx.createMediaStreamSource(this.micBarsStream);

          this.micBarsAnalyser = audioCtx.createAnalyser();
          this.micBarsAnalyser.fftSize = 512;
          this.micBarsAnalyser.smoothingTimeConstant = 0.8;
          this.micSourceNode.connect(this.micBarsAnalyser);
        } catch (e) {
          this.resetBars();
          return;
        }
      }

      const analyser = this.micBarsAnalyser;
      if (!analyser) {
        this.resetBars();
        return;
      }

      const data = new Uint8Array(analyser.fftSize);
      const base = 6;
      const animate = () => {
        if (this.callState !== CallState.ACTIVE || this.isMuted) {
          this.stopMicBarsAnimation();
          this.resetBars();
          return;
        }
        analyser.getByteTimeDomainData(data);
        let sumSquares = 0;
        for (let i = 0; i < data.length; i++) {
          const normalized = (data[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }
        const rms = Math.sqrt(sumSquares / data.length);
        const targetScale = Math.max(0, rms * 60 - 0.8);
        this.smoothedMicScale = this.smoothedMicScale * 0.82 + targetScale * 0.18;
        const scale = this.smoothedMicScale;
        const maxBarHeight = 28;
        const restEnvelope = [0, 4, 8, 4, 0];
        const reactivity = [0.8, 1.0, 1.2, 1.0, 0.8];
        this.bars = restEnvelope.map((rest, i) => Math.min(maxBarHeight, base + rest + scale * reactivity[i]));
        this.micBarsAnimationId = requestAnimationFrame(animate);
      };
      this.micBarsAnimationId = requestAnimationFrame(animate);
    } finally {
      this.micBarsStarting = false;
    }
  }

  private stopMicBarsAnimation() {
    if (this.micBarsAnimationId !== null) {
      cancelAnimationFrame(this.micBarsAnimationId);
      this.micBarsAnimationId = null;
    }
  }

  private destroyMicBarsMonitor() {
    this.micSourceNode?.disconnect();
    this.micSourceNode = null;
    this.micBarsStream = null;
    this.micBarsAnalyser = null;
    this.smoothedMicScale = 0;
  }

  public get sessionDurationMinutes(): number {
    return Math.floor((this.sessionSummaryData?.durationMs || 0) / 60000);
  }

  public get sessionDurationSeconds(): number {
    return Math.floor(((this.sessionSummaryData?.durationMs || 0) % 60000) / 1000);
  }
}
